import React, { useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Text, TextLink } from '@weave/design-system';
import {
  ChecklistMetricStatus,
  ExceptionChangeParams,
  MultiLocationResult,
} from '../../handoff-checklist-v2.types';
import { getStatusIcon } from '../../utils/status-icon';
import { ExceptionCell } from './exception-cell';

export type { ExceptionChangeParams };

export interface MultiLocationMetricTableProps {
  metricId: string;
  locationResults: MultiLocationResult[];
  allowExceptions?: boolean;
  showWhereToFix?: boolean;
  readOnly?: boolean;
  onExceptionChange?: (params: ExceptionChangeParams) => void;
}

export const MultiLocationMetricTable = ({
  metricId,
  locationResults,
  allowExceptions = false,
  showWhereToFix = false,
  readOnly = false,
  onExceptionChange,
}: MultiLocationMetricTableProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const getEditingKey = (locationId: string, criteriaIndex: number) =>
    `${locationId}:${criteriaIndex}`;

  const hasWhereToFix =
    showWhereToFix &&
    locationResults.some((loc) =>
      loc.criterias.some((c) => c.status !== ChecklistMetricStatus.PASSED && c.whereToFix)
    );

  return (
    <div css={styles.container}>
      <table css={styles.table}>
        <thead>
          <tr>
            <th css={[styles.headerCell, styles.locationHeader]}>Location</th>
            <th css={[styles.headerCell, styles.criteriaHeader]}>Criteria</th>
            <th css={[styles.headerCell, styles.resultHeader]}>Result</th>
            {hasWhereToFix && (
              <th css={[styles.headerCell, styles.whereToFixHeader]}>Where to Fix</th>
            )}
            {allowExceptions && (
              <th css={[styles.headerCell, styles.exceptionHeader]}>Exceptions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {locationResults.map((location, locIdx) => {
            const criteriaCount = location.criterias.length;

            if (criteriaCount === 0) {
              return (
                <tr
                  key={location.locationId}
                  css={[styles.row, locIdx > 0 && styles.locationGroupBorder]}
                >
                  <td css={styles.locationCell}>
                    <Text size="medium" as="div">
                      {location.locationName ?? location.locationId}
                    </Text>
                  </td>
                  <td css={styles.cell}>
                    <Text size="small" color="light" as="div">
                      —
                    </Text>
                  </td>
                  <td css={styles.cell}>
                    <Text size="small" color="light" as="div">
                      —
                    </Text>
                  </td>
                  {hasWhereToFix && (
                    <td css={styles.cell}>
                      <Text size="small" color="light" as="div">
                        —
                      </Text>
                    </td>
                  )}
                  {allowExceptions && (
                    <td css={styles.cell}>
                      <Text size="small" color="light" as="div">
                        —
                      </Text>
                    </td>
                  )}
                </tr>
              );
            }
            return location.criterias.map((criterion, critIdx) => {
              const isFirstCriterion = critIdx === 0;
              const StatusIcon = getStatusIcon(criterion.status);
              const key = getEditingKey(location.locationId, critIdx);
              const isPassed = criterion.status === ChecklistMetricStatus.PASSED;
              const resultText = isPassed ? 'Passed' : criterion.message;

              return (
                <tr
                  key={`${location.locationId}:${critIdx}`}
                  css={[
                    styles.row,
                    isFirstCriterion && locIdx > 0 && styles.locationGroupBorder,
                  ]}
                >
                  {isFirstCriterion && (
                    <td rowSpan={criteriaCount} css={styles.locationCell}>
                      <Text size="medium" as="div">
                        {location.locationName ?? location.locationId}
                      </Text>
                    </td>
                  )}

                  <td css={styles.cell}>
                    <Text size="medium" as="div">
                      {criterion.description}
                    </Text>
                  </td>

                  <td css={styles.cell}>
                    {criterion.messageNode ?? (
                      <div css={styles.criterionContent}>
                        <div css={styles.criterionHeader}>
                          {StatusIcon && <StatusIcon css={styles.statusSvg} />}
                          <div css={styles.criterionText}>
                            {resultText && (
                              <Text
                                size="medium"
                                as="span"
                                css={!isPassed ? { fontWeight: 'bold' } : undefined}
                              >
                                {resultText}
                              </Text>
                            )}

                            {criterion.actualValue !== undefined && (
                              <div>
                                <Text size="medium" weight="bold" as="span">
                                  Actual:{' '}
                                </Text>
                                <Text size="medium" as="span">
                                  {criterion.actualValue}
                                </Text>
                              </div>
                            )}

                            {!resultText && criterion.actualValue === undefined && (
                              <Text size="medium" as="span">
                                —
                              </Text>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>

                  {hasWhereToFix && (
                    <td css={styles.cell}>
                      {criterion.status !== ChecklistMetricStatus.PASSED &&
                      criterion.whereToFix ? (
                        <TextLink
                          href={criterion.whereToFix.url}
                          rel="noopener noreferrer"
                          target="_blank"
                          size="small"
                          weight="bold"
                        >
                          {criterion.whereToFix.label}
                        </TextLink>
                      ) : (
                        <Text size="small" color="light" as="div">
                          —
                        </Text>
                      )}
                    </td>
                  )}

                  {allowExceptions && (
                    <td css={styles.cell}>
                      <ExceptionCell
                        exception={criterion.exception || ''}
                        metricId={metricId}
                        criteriaIndex={critIdx}
                        isEditing={editingKey === key}
                        readOnly={readOnly}
                        allowException={
                          criterion.status !== ChecklistMetricStatus.PASSED &&
                          criterion.exceptionType !== undefined &&
                          criterion.exceptionType !== 'none'
                        }
                        exceptionOptions={criterion.exceptionOptions}
                        exceptionType={criterion.exceptionType}
                        onStartEdit={() => setEditingKey(key)}
                        onStopEdit={() => setEditingKey(null)}
                        onExceptionChange={
                          onExceptionChange
                            ? (_metricId, _criteriaIndex, exception) =>
                                onExceptionChange({
                                  metricId,
                                  locationId: location.locationId,
                                  criteriaIndex: critIdx,
                                  exception,
                                })
                            : undefined
                        }
                      />
                    </td>
                  )}
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
};

const styles = {
  container: css`
    background-color: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    overflow: hidden;
  `,
  table: css`
    width: 100%;
    border-collapse: collapse;
    margin: 0;
    table-layout: fixed;
  `,
  headerCell: css`
    background-color: #f4f5f7;
    color: #8f95a3;
    font-size: 14px;
    font-weight: 700;
    text-align: left;
    padding: ${theme.spacing(1.5, 2)};
    padding-bottom: ${theme.spacing(2)};
    border-bottom: 0;
  `,
  locationHeader: css`
    width: 15%;
    min-width: 120px;
  `,
  criteriaHeader: css`
    width: 30%;
  `,
  resultHeader: css`
    width: 30%;
  `,
  whereToFixHeader: css`
    width: 15%;
  `,
  exceptionHeader: css`
    width: 25%;
    max-width: 250px;
  `,
  row: css`
    border-bottom: 1px solid #f0f0f0;

    &:last-of-type {
      border-bottom: 0;
    }
  `,
  locationGroupBorder: css`
    border-top: 1px solid #e8e8e8;
  `,
  locationCell: css`
    padding: ${theme.spacing(2.5, 2)};
    vertical-align: middle;
    border-right: 1px solid #f0f0f0;
  `,
  cell: css`
    padding: ${theme.spacing(2, 2)};
    vertical-align: middle;
    word-break: break-word;
  `,
  criterionContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  criterionText: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  criterionHeader: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `,
  statusSvg: css`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  `,
};
